import React, { useMemo } from 'react'
import { FormProvider, useFormContext, UseFormReturn } from 'react-hook-form'
import { Panel } from '@components/Panel/Panel'
import { Token } from '@components/Tokens/TokenSelector/tokenSchemas'
import { TokenSelection } from '@components/Tokens/TokenSelector/TokenSelection'
import { TokenType } from '@components/Tokens/types'
import { FormRender, Icon, Stack, Text } from '@ui'
import { useNftMetadata } from 'api/lib/tokenContracts'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useEnvironment } from 'hooks/useEnvironment'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    SpaceInfo,
    useContractSpaceInfoWithoutClient,
    useDefaultOwnershipCooldown,
    useGuardianStatus,
    useIsSpaceOwner,
    useMyLinkedWallets,
} from 'use-towns-client'
import { Address, isAddress } from 'viem'

import { TransferSchema, transferSchema } from '../transferAssetsSchema'
import { WalletSelector } from '../WalletSelector'

import { SubmitButton } from './SubmitButton'
import { TransferState } from './types'

export function TransferOwnershipPanel() {
    const spaceId = useSpaceIdFromPathname()
    const { data: myAbstractWalletAddress } = useMyAbstractAccountAddress()
    const spaceOwnerContractAddress = useEnvironment().baseChainConfig.addresses.spaceOwner
    const { data: spaceInfo } = useContractSpaceInfoWithoutClient(spaceId)
    const { isOwner } = useIsSpaceOwner(spaceId, myAbstractWalletAddress)

    // if the membership nft is in another wallet, this doesn't really apply
    if (!isOwner) {
        return null
    }

    return (
        <Panel label="Transfer Ownership" padding="none" data-testid="transfer-assets-panel">
            <TransferOwnershipContent
                spaceInfo={spaceInfo}
                spaceOwnerContractAddress={spaceOwnerContractAddress}
            />
        </Panel>
    )
}

function TransferOwnershipContent(props: {
    spaceInfo: SpaceInfo | undefined
    spaceOwnerContractAddress: string
}) {
    const { spaceInfo, spaceOwnerContractAddress } = props
    const getPanelTitle = () => {
        return 'Transfer Ownership'
    }

    const chainId = useEnvironment().baseChain.id

    const tokenId = spaceInfo?.tokenId
    const { data: tokenData } = useNftMetadata({
        chainId,
        tokenId: tokenId ?? '',
        contractAddress: spaceOwnerContractAddress ?? '',
    })

    const token = nftToToken(tokenData, chainId)

    const { data: myAbstractWalletAddress } = useMyAbstractAccountAddress()
    const { data: linkedWallets, isLoading: isLinkedWalletsLoading } = useMyLinkedWallets()

    const { data: guardianStatus, isLoading: isGuardianStatusLoading } = useGuardianStatus({
        walletAddress: myAbstractWalletAddress,
        enabled: !!myAbstractWalletAddress,
    })

    const { data: defaultOwnershipCooldown } = useDefaultOwnershipCooldown()
    const defaultCooldownTime = formatCooldownTime(defaultOwnershipCooldown ?? 0n)

    const transferState: TransferState = useMemo(() => {
        const { isGuardianEnabled, guardianCooldown } = guardianStatus ?? {}

        // guardian has been disabled, the cooldown period has been met
        if (!isGuardianEnabled) {
            return 'ready'
        }

        // guardian still enabled, waiting on cooldown to end
        if (isGuardianEnabled && guardianCooldown && guardianCooldown > 0n) {
            return 'cooldown-initiated'
        }

        return 'none'
    }, [guardianStatus])

    const pendingCooldownTime = useMemo(() => {
        if (transferState === 'cooldown-initiated' && guardianStatus?.guardianCooldown) {
            return formatRemainingCooldown(guardianStatus.guardianCooldown)
        }
    }, [guardianStatus, transferState])

    const infoText = useMemo(() => {
        if (transferState === 'none') {
            return `Transferring town ownership requires a ${defaultCooldownTime} waiting period before you can complete the transfer.`
        }
        if (transferState === 'cooldown-initiated') {
            return `Your transfer is pending and you will be able to complete the transfer in ${pendingCooldownTime}s.`
        }
        return
    }, [transferState, defaultCooldownTime, pendingCooldownTime])

    const defaultValues: TransferSchema | undefined = useMemo(() => {
        if (!token) {
            return
        }
        return {
            // defaulting to zero address b/c we have 3 different possible ops to be submitted
            // only one of which requires a recipient
            // but the schema requires a recipient
            // so there is additional validation in the submit function
            recipient: undefined,
            assetAddressOrIdentifier: token.data.address as Address,
            tokenId: token.data.tokenId,
        }
    }, [token])

    return (
        <Panel label={getPanelTitle()} padding="none" data-testid="transfer-assets-panel">
            <Stack padding height="100%">
                {isGuardianStatusLoading || isLinkedWalletsLoading || !token ? (
                    <ButtonSpinner />
                ) : (
                    <>
                        <Stack gap>
                            {token && <TokenSelection token={token} />}
                            {infoText && (
                                <Stack
                                    horizontal
                                    rounded="sm"
                                    background="level2"
                                    padding="md"
                                    gap="sm"
                                    alignItems="center"
                                >
                                    <Icon type="info" color="gray2" size="square_sm" />
                                    <Text size="sm">{infoText}</Text>
                                </Stack>
                            )}
                        </Stack>

                        <FormRender
                            height="100%"
                            schema={transferSchema}
                            defaultValues={defaultValues}
                            id="TransferOwnership"
                            mode="onChange"
                        >
                            {(form: UseFormReturn<TransferSchema>) => (
                                <FormProvider {...form}>
                                    {transferState === 'ready' && (
                                        <Stack gap>
                                            <WalletSelector
                                                aaAddress={myAbstractWalletAddress}
                                                fromWallet={myAbstractWalletAddress}
                                                linkedWallets={linkedWallets}
                                                showBalance={false}
                                            />
                                            {linkedWallets && (
                                                <WarningBanner linkedWallets={linkedWallets} />
                                            )}
                                        </Stack>
                                    )}
                                    <SubmitButton
                                        tokenId={tokenId}
                                        tokenName={token?.data.label}
                                        tokenImgSrc={token?.data.imgSrc}
                                        transferState={transferState}
                                        defaultCooldownTime={defaultCooldownTime}
                                        spaceInfo={spaceInfo}
                                    />
                                </FormProvider>
                            )}
                        </FormRender>
                    </>
                )}
            </Stack>
        </Panel>
    )
}

function WarningBanner(props: { linkedWallets: string[] }) {
    const { linkedWallets } = props
    const { watch } = useFormContext<TransferSchema>()
    const recipient = watch('recipient')
    const isRecipientVerified = useMemo(() => {
        return (
            !!recipient &&
            isAddress(recipient) &&
            linkedWallets.some((wallet) => wallet.toLowerCase() === recipient.toLowerCase())
        )
    }, [recipient, linkedWallets])

    if (!recipient || isRecipientVerified) {
        return null
    }

    return (
        <Stack
            horizontal
            gap
            rounded="sm"
            background="warningSubtle"
            padding="md"
            alignItems="center"
        >
            <Icon type="warning" size="square_sm" />
            <Text size="sm">
                Warning! You are about to send an asset to an unverified address. Please confirm
                that it is a Base compatible EVM wallet, otherwise you may lose your asset and it
                will not be recoverable
            </Text>
        </Stack>
    )
}

export function nftToToken(
    nft: ReturnType<typeof useNftMetadata>['data'],
    chainId: number,
): Token | undefined {
    if (!nft) {
        return undefined
    }
    const contractAddress = nft.data.contract.address
    const tokenId = nft.data.id
    if (!contractAddress || !tokenId) {
        return undefined
    }
    //console.log('nft', nft.data)
    return {
        chainId,
        data: {
            address: contractAddress,
            type: TokenType.ERC721,
            label: nft.data.name ?? '',
            imgSrc: nft.data.image?.cachedUrl ?? '',
            tokenId,
        },
    }
}

function formatCooldownTime(seconds: bigint): string {
    const SECONDS_PER_MINUTE = 60n
    const MINUTES_PER_HOUR = 60n
    const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR

    const totalMinutes = seconds / SECONDS_PER_MINUTE

    if (totalMinutes < MINUTES_PER_HOUR) {
        return `${totalMinutes} minute`
    }

    const hours = seconds / SECONDS_PER_HOUR
    return `${hours} hour`
}

function getCooldownRemaining(expiryTimestamp: bigint): bigint {
    const currentTimestamp = BigInt(Math.floor(Date.now() / 1000))
    const remaining = expiryTimestamp - currentTimestamp
    return remaining > 0n ? remaining : 0n
}

function formatRemainingCooldown(expiryTimestamp: bigint): string {
    const remainingSeconds = getCooldownRemaining(expiryTimestamp)
    return formatCooldownTime(remainingSeconds)
}
